import { Dialog, DialogTrigger } from "./Dialog";
import { Button } from "./Button";
import { DialogContent, DialogDescription, DialogTitle, DialogClose } from "./Dialog";
import { useState, useEffect } from "react";
import { useSubscription } from "../hooks/useSubscription";
import { formatDate } from "../utils/formatDate";
import cn from "clsx";
import { InfoIcon, Loader2 } from "lucide-react";
import { limitSubscriptions } from "../utils/limitSubscriptions";
import { useGlobal } from "../context/globalProvider";
import { toast } from "../hooks/useToast";
import { Charge, CreateSubscriptionRequest } from "@rechargeapps/storefront-client";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "./Alert"

const SubscriptionAddDialog = ({ 
  product, 
  nextOrder, 
  firstVariant,
  source,
  Trigger,
  sale,
  onSuccess,
  subscriptions = [],
  properties = []
}: { 
  product: any, 
  firstVariant: any,
  source: string,
  Trigger: React.ReactNode,
  sale?: boolean,
  nextOrder?: Charge,
  subscriptions?: CreateSubscriptionRequest[],
  onSuccess?: () => void
  properties?: any[]
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { createSubscription, isLoading, getProductPlans, createSubscriptions } = useSubscription();
  const { customer, sendEvent } = useGlobal();

  useEffect(() => {
    if (isOpen && source?.includes('banner')) {
      sendEvent('subscription_dialog_opened', { source });
    }
  }, [isOpen, source]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {Trigger}
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogTitle>Add Subscription</DialogTitle>
        <DialogDescription>
          {
            nextOrder?.scheduled_at ? (
              `Adding this subscription will add ${product.title} to your ${formatDate(nextOrder?.scheduled_at)} order.`
            ) : (
              `Adding this subscription will create a new ${product.title} subscription starting today.`
            )
          }

          {
            product.id === "9374046978284" ? (
              <Alert variant="default" className="mt-3">
                {/* <InfoIcon /> */}
                <AlertDescription>
                  When you purchase as a subscription, your first shipment will be Pumpkin Chai. After that, your subscription automatically returns to our original Vanilla flavor.
                </AlertDescription>
              </Alert>
            ) : null
          }
        </DialogDescription>
        <div className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button disabled={isLoading} onClick={async () => {
            if (subscriptions && subscriptions.length > 0) {
              const subscriptionsToCreate = [];
              for (const product of subscriptions) {
                const plans = await getProductPlans(product.external_product_id?.ecommerce || "");
                const subscriptionPlans = plans?.filter((plan) => plan.type === 'subscription');
                subscriptionsToCreate.push({
                  ...product,
                  charge_interval_frequency: subscriptionPlans?.[0]?.subscription_preferences.charge_interval_frequency,
                  charge_interval_unit_type: subscriptionPlans?.[0]?.subscription_preferences.interval_unit,
                  order_interval_frequency: subscriptionPlans?.[0]?.subscription_preferences.charge_interval_frequency,
                  order_interval_unit: subscriptionPlans?.[0]?.subscription_preferences.interval_unit,
                  properties,
                });
              }

              await createSubscriptions(subscriptionsToCreate, source);
              setIsOpen(false);
              onSuccess?.();
              return;
            }

            const plans = await getProductPlans(product.id);
            const subscriptionPlans = plans?.filter((plan) => plan.type === 'subscription');
            const { allowed } = limitSubscriptions(customer?.include?.subscriptions || [], product);

            if (!allowed) {
              toast({
                title: 'You already have this subscription. Cannot add another.',
                variant: 'destructive',
              });
              return;
            }

            const productIsRoutineBuilder = false;

            await createSubscription({
              address_id: Number(nextOrder?.address_id || customer?.include?.addresses?.[0]?.id),
              external_variant_id: {
                  ecommerce: firstVariant.id.toString(),
              },
              quantity: 1,
              next_charge_scheduled_at: nextOrder?.scheduled_at || new Date().toISOString(),
              plan_id: subscriptionPlans?.[0]?.id,
              properties: properties || undefined,
            }, source, productIsRoutineBuilder ? {
                addDiscountCode: "BYESUMMER",
                chargeCodes: nextOrder?.discounts || []
              } : {})

            setIsOpen(false);
            onSuccess?.();
          }}>
            <span className={cn({ "opacity-0": isLoading })}>Yes, add to my order</span>
            { isLoading && <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><Loader2 className="w-4 h-4 animate-spin" /></span> }
          </Button>
        </div>
      </DialogContent>

    </Dialog>
  )
}

export default SubscriptionAddDialog;